import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { Key } from '../../global/enum/global.enum';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private readonly refreshHttp: HttpClient;

  constructor(handler: HttpBackend) {
    // HttpBackend bypasses all interceptors — prevents circular calls on refresh
    this.refreshHttp = new HttpClient(handler);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    if (this.shouldBypass(request)) {
      return next.handle(request);
    }

    const token = localStorage.getItem(Key.TOKEN);
    const requestWithCredentials = request.clone({ withCredentials: true });
    const authorizedRequest = token
      ? this.addAuthorizationTokenHeader(requestWithCredentials, token)
      : requestWithCredentials;

    return next.handle(authorizedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        const currentToken = localStorage.getItem(Key.TOKEN);
        const isAuthError = error.status === 401 || error.status === 403;

        if (currentToken && isAuthError) {
          return this.handleTokenExpiry(request, next, error);
        }

        return throwError(() => error);
      })
    );
  }

  private handleTokenExpiry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<any> {
    const refreshToken = localStorage.getItem(Key.REFRESH_TOKEN);

    if (!refreshToken) {
      this.clearAndReload();
      return throwError(() => originalError);
    }

    if (this.isRefreshing) {
      // Another request triggered a refresh — wait for the new token then retry
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((newToken) =>
          next.handle(
            this.addAuthorizationTokenHeader(
              request.clone({ withCredentials: true }),
              newToken!
            )
          )
        )
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.refreshHttp
      .get<any>(`${environment.domain}/user/refresh/token`, {
        headers: { Authorization: `Bearer ${refreshToken}` },
      })
      .pipe(
        tap((response: any) => {
          const newToken =
            response?.data?.access_token ?? response?.access_token;
          localStorage.setItem(Key.TOKEN, newToken);
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
        }),
        switchMap((response: any) => {
          const newToken =
            response?.data?.access_token ?? response?.access_token;
          return next.handle(
            this.addAuthorizationTokenHeader(
              request.clone({ withCredentials: true }),
              newToken
            )
          );
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          this.clearAndReload();
          return throwError(() => refreshError);
        })
      );
  }

  private clearAndReload(): void {
    localStorage.removeItem(Key.TOKEN);
    localStorage.removeItem(Key.REFRESH_TOKEN);
    location.reload();
  }

  private shouldBypass(request: HttpRequest<unknown>): boolean {
    const url = request.url;

    const publicGetRoutes = [
      'home/hero',
      'careers/all',
      'expertise/all',
      'projects/all',
      'projects/all/details',
      'contacts/all',
      'news/all',
      'media/video',
    ];

    if (
      request.method === 'GET' &&
      publicGetRoutes.some((route) => url.includes(route))
    ) {
      return true;
    }

    return (
      url.includes('login') ||
      url.includes('refresh') ||
      (url.includes('api/about/story') &&
        !url.includes('api/about/story/update') &&
        !url.includes('api/about/story/update/file') &&
        !url.includes('api/about/story/delete/story-part') &&
        !url.includes('api/about/story/create/story-part')) ||
      (url.includes('api/about/leadership') &&
        !url.includes('api/about/leadership/update') &&
        !url.includes('api/about/leadership/update/file') &&
        !url.includes('api/about/leadership/delete/leadership-part') &&
        !url.includes('api/about/leadership/create/leadership-part')) ||
      (url.includes('api/about/policy') &&
        !url.includes('api/about/policy/update') &&
        !url.includes('api/about/policy/update/file') &&
        !url.includes('api/about/policy/delete/policy-part') &&
        !url.includes('api/about/policy/create/policy-part')) ||
      url.includes('send-email') ||
      url.includes('send-resume')
    );
  }

  private addAuthorizationTokenHeader(
    request: HttpRequest<unknown>,
    token: string | null
  ): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
